import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronRight, ChevronLeft, Loader2, Plus, Trash2, User, Home, ClipboardList, GraduationCap, Landmark, Baby, PenTool, LogOut, CheckCircle2, AlertTriangle, Search, MapPin } from 'lucide-react';
import KkovSelect from '../components/KkovSelect';

const STEPS = [
    { id: 'osobni',    label: 'Osobní údaje',       Icon: User },
    { id: 'adresa',    label: 'Bydliště a kontakt', Icon: Home },
    { id: 'evidence',  label: 'Evidenční údaje',    Icon: ClipboardList },
    { id: 'vzdelani',  label: 'Vzdělání a další',   Icon: GraduationCap },
    { id: 'finance',   label: 'Finance a právní',   Icon: Landmark },
    { id: 'deti',      label: 'Vyživované osoby',   Icon: Baby },
    { id: 'podpis',    label: 'Podpis a odeslání',  Icon: PenTool },
];

const inputStyle = {
    background: 'var(--surface-elevated)',
    border: '1px solid var(--border-default)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
    padding: '8px 12px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.15s',
    fontSize: '14px',
    colorScheme: 'inherit',
};

const labelStyle = { color: 'var(--text-muted)', fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' };

// ── Parser rodného čísla → datum narození + pohlaví ─────────
function parseRodneCislo(rc) {
    if (!rc) return null;
    const clean = rc.replace(/[\s/]/g, '');
    if (!/^\d{9,10}$/.test(clean)) return null;

    let year = parseInt(clean.substring(0, 2), 10);
    let month = parseInt(clean.substring(2, 4), 10);
    const day = parseInt(clean.substring(4, 6), 10);

    // Pohlaví: ženy mají měsíc +50 (nebo +70 po roce 2004)
    let pohlavi = 'muz';
    if (month > 70) { pohlavi = 'zena'; month -= 70; }
    else if (month > 50) { pohlavi = 'zena'; month -= 50; }
    else if (month > 20) { month -= 20; } // muži po 2004

    // Století: 9 číslic = před 1954, 10 číslic = rozlišit dle YY
    if (clean.length === 9) {
        year += 1900;
    } else {
        year += year < 54 ? 2000 : 1900;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    // Kontrola dělitelností 11 (pro 10místné RČ)
    if (clean.length === 10 && parseInt(clean, 10) % 11 !== 0) return null;

    const datum = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { datum, pohlavi };
}

const RODINNY_STAV_OPTIONS = ['Svobodný/á', 'Ženatý/Vdaná', 'Rozvedený/á', 'Vdovec/Vdova', 'Druh/Družka'];
const VZDELANI_OPTIONS = ['Základní', 'Střední odborné (SOU)', 'Střední s maturitou', 'Vyšší odborné (VOS)', 'Vysokoškolské'];
const DUCHOD_OPTIONS = ['', 'Starobní', 'Invalidní I. stupně', 'Invalidní II. stupně', 'Invalidní III. stupně'];
const ZDR_OMEZENI_OPTIONS = ['', 'I', 'II', 'III', 'OZZ'];

function Field({ label, required, children }) {
    return (
        <div>
            <label style={labelStyle}>{label}{required && <span style={{ color: '#f87171' }}> *</span>}</label>
            {children}
        </div>
    );
}

function Input({ value, onChange, type = 'text', placeholder, readOnly, prefilled, ...rest }) {
    const [focused, setFocused] = useState(false);
    return (
        <div>
            <input
                type={type}
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                readOnly={readOnly}
                style={{
                    ...inputStyle,
                    ...(focused ? { borderColor: 'var(--brand)' } : {}),
                    ...(readOnly || prefilled ? {
                        background: 'var(--surface-base)',
                        borderStyle: 'dashed',
                        borderColor: 'var(--border-default)',
                        opacity: 0.85,
                        cursor: readOnly ? 'not-allowed' : 'default',
                    } : {}),
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                {...rest}
            />
            {(readOnly || prefilled) && (
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', display: 'block' }}>
                    {readOnly ? 'Předvyplněno — nelze upravit' : 'Automaticky doplněno'}
                </span>
            )}
        </div>
    );
}

function Select({ value, onChange, options, placeholder }) {
    const [focused, setFocused] = useState(false);
    return (
        <select
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            style={{
                ...inputStyle,
                ...(focused ? { borderColor: 'var(--brand)' } : {}),
                appearance: 'auto',
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
        >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(o => (
                <option key={typeof o === 'object' ? o.value : o} value={typeof o === 'object' ? o.value : o}>
                    {typeof o === 'object' ? o.label : o}
                </option>
            ))}
        </select>
    );
}

function RadioGroup({ value, onChange, options }) {
    return (
        <div className="flex gap-3 flex-wrap">
            {options.map(o => {
                const val = typeof o === 'object' ? o.value : o;
                const label = typeof o === 'object' ? o.label : o;
                const active = value === val;
                return (
                    <button
                        key={val}
                        type="button"
                        onClick={() => onChange(val)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                        style={{
                            background: active ? 'var(--brand-bg)' : 'var(--surface-elevated)',
                            border: `1px solid ${active ? 'var(--brand)' : 'var(--border-default)'}`,
                            color: active ? 'var(--brand-text)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                        }}
                    >
                        <div style={{
                            width: 14, height: 14, borderRadius: '50%',
                            border: `2px solid ${active ? 'var(--brand)' : 'var(--border-strong)'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }} />}
                        </div>
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function Checkbox({ checked, onChange, label }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--text-secondary)' }}>
            <div
                onClick={() => onChange(!checked)}
                style={{
                    width: 18, height: 18, borderRadius: 3, flexShrink: 0,
                    border: `2px solid ${checked ? 'var(--brand)' : 'var(--border-strong)'}`,
                    background: checked ? 'var(--brand)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s',
                }}
            >
                {checked && <Check size={12} color="white" strokeWidth={3} />}
            </div>
            <span onClick={() => onChange(!checked)}>{label}</span>
        </label>
    );
}

// ── Signature Pad ───────────────────────────────────────────
function SignaturePad({ value, onChange }) {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    const getPos = useCallback((e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const touch = e.touches ? e.touches[0] : e;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }, []);

    const startDraw = useCallback((e) => {
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const pos = getPos(e);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        setDrawing(true);
    }, [getPos]);

    const draw = useCallback((e) => {
        if (!drawing) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const pos = getPos(e);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = 'var(--text-primary)';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        setHasDrawn(true);
    }, [drawing, getPos]);

    const endDraw = useCallback(() => {
        if (!drawing) return;
        setDrawing(false);
        if (canvasRef.current) {
            onChange(canvasRef.current.toDataURL('image/png'));
        }
    }, [drawing, onChange]);

    const clear = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
        onChange('');
    }, [onChange]);

    // Redraw if value is restored (e.g. navigating back)
    useEffect(() => {
        if (value && canvasRef.current && !hasDrawn) {
            const img = new Image();
            img.onload = () => {
                canvasRef.current.getContext('2d').drawImage(img, 0, 0);
                setHasDrawn(true);
            };
            img.src = value;
        }
    }, [value, hasDrawn]);

    return (
        <div>
            <canvas
                ref={canvasRef}
                width={500}
                height={150}
                style={{
                    width: '100%', maxWidth: 500, height: 150,
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 6, cursor: 'crosshair', touchAction: 'none',
                }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
            />
            {hasDrawn && (
                <button
                    type="button"
                    onClick={clear}
                    className="mt-2 text-xs px-3 py-1 rounded-lg"
                    style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)' }}
                >
                    Vymazat podpis
                </button>
            )}
        </div>
    );
}

// ── Step Components ─────────────────────────────────────────

function StepOsobni({ form, setField }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Jméno" required><Input value={form.jmeno} readOnly onChange={() => {}} /></Field>
            <Field label="Příjmení" required><Input value={form.prijmeni} readOnly onChange={() => {}} /></Field>
            <Field label="Titul před jménem"><Input value={form.titul_pred} onChange={v => setField('titul_pred', v)} placeholder="Ing., Bc., ..." /></Field>
            <Field label="Titul za jménem"><Input value={form.titul_za} onChange={v => setField('titul_za', v)} placeholder="Ph.D., MBA, ..." /></Field>
            <Field label="Telefon" required><Input value={form.telefon} onChange={v => setField('telefon', v)} placeholder="+420 ..." /></Field>
            <Field label="Email" required><Input value={form.email} readOnly onChange={() => {}} /></Field>
        </div>
    );
}

function AddressValidation({ ulice, mesto, psc }) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!mesto || !psc || psc.replace(/\s/g, '').length < 5) {
            setResult(null);
            return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ mesto, psc, ulice: ulice || '' });
                const resp = await fetch(`/api/validace/adresa?${params}`);
                if (resp.ok) setResult(await resp.json());
                else setResult(null);
            } catch { setResult(null); }
            setLoading(false);
        }, 600);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [ulice, mesto, psc]);

    if (loading) return <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Ověřuji adresu...</p>;
    if (!result) return null;

    if (result.valid === true) {
        return (
            <div className="flex items-center gap-1.5 mt-1">
                <CheckCircle2 size={13} style={{ color: '#10b981' }} />
                <span className="text-xs" style={{ color: '#10b981' }}>Adresa nalezena v RUIAN</span>
            </div>
        );
    }

    if (result.valid === null) {
        return (
            <div className="flex items-center gap-1.5 mt-1">
                <AlertTriangle size={13} style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{result.message}</span>
            </div>
        );
    }

    return (
        <div className="mt-1">
            <div className="flex items-center gap-1.5">
                <AlertTriangle size={13} style={{ color: '#f59e0b' }} />
                <span className="text-xs" style={{ color: '#f59e0b' }}>{result.message || 'Adresa nebyla nalezena v registru RUIAN'}</span>
            </div>
            {result.suggestions?.length > 0 && (
                <div className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                    Návrhy: {result.suggestions.slice(0, 3).join(' | ')}
                </div>
            )}
        </div>
    );
}

function AddressSuggestInput({ value, onChange, onSelectAddress, placeholder }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const timerRef = useRef(null);
    const wrapRef = useRef(null);
    const [verified, setVerified] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleChange = (v) => {
        onChange(v);
        setVerified(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (v.length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const resp = await fetch(`/api/validace/adresa/suggest?q=${encodeURIComponent(v)}`);
                if (resp.ok) {
                    const data = await resp.json();
                    setSuggestions(data);
                    setOpen(data.length > 0);
                }
            } catch { /* ignore */ }
            setLoading(false);
        }, 400);
    };

    const handleSelect = async (suggestion) => {
        setOpen(false);
        setSuggestions([]);
        onChange(suggestion.text);
        setLoading(true);
        try {
            const resp = await fetch(`/api/validace/adresa/resolve?magicKey=${encodeURIComponent(suggestion.magicKey)}`);
            if (resp.ok) {
                const data = await resp.json();
                if (data.ulice || data.mesto || data.psc) {
                    onSelectAddress(data.ulice, data.mesto, data.psc);
                    setVerified(true);
                }
            }
        } catch { /* ignore */ }
        setLoading(false);
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <div className="flex items-center gap-2" style={{
                ...inputStyle,
                ...(focused ? { borderColor: 'var(--brand)' } : {}),
                ...(verified ? { borderColor: '#10b981' } : {}),
                padding: 0,
            }}>
                <span style={{ padding: '8px 0 8px 12px', display: 'flex', flexShrink: 0 }}>
                    {loading ? <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                        : verified ? <CheckCircle2 size={14} style={{ color: '#10b981' }} />
                        : <Search size={14} style={{ color: 'var(--text-muted)' }} />}
                </span>
                <input
                    type="text"
                    value={value ?? ''}
                    onChange={e => handleChange(e.target.value)}
                    onFocus={() => { setFocused(true); if (suggestions.length > 0) setOpen(true); }}
                    onBlur={() => setFocused(false)}
                    placeholder={placeholder || 'Začněte psát adresu...'}
                    style={{
                        flex: 1, border: 'none', outline: 'none', background: 'transparent',
                        color: 'var(--text-primary)', fontSize: '14px', padding: '8px 12px 8px 0',
                    }}
                />
            </div>
            {verified && (
                <span style={{ fontSize: '10px', color: '#10b981', marginTop: '2px', display: 'block' }}>
                    Adresa ověřena z registru RUIAN
                </span>
            )}
            {open && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
                    borderRadius: '8px', marginTop: '4px', maxHeight: '220px', overflowY: 'auto',
                    boxShadow: 'var(--shadow-md)',
                }}>
                    {suggestions.map((s, i) => (
                        <button key={i} type="button"
                            onClick={() => handleSelect(s)}
                            className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2"
                            style={{
                                color: 'var(--text-primary)', background: 'transparent',
                                border: 'none', cursor: 'pointer',
                                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-default)' : 'none',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-elevated)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <MapPin size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <span className="truncate">{s.text}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function StepAdresa({ form, setField }) {
    const [sameAddress, setSameAddress] = useState(true);

    const handleSelectAddress = (prefix) => (ulice, mesto, psc) => {
        setField(prefix + 'ulice', ulice);
        if (mesto) setField(prefix + 'mesto', mesto);
        if (psc) setField(prefix + 'psc', psc);
    };

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Kontaktní e-mail</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Field label="E-mail pro notifikace (odlišný od přihlašovacího)" required>
                            <Input type="email" value={form.kontaktni_email} onChange={v => setField('kontaktni_email', v)} placeholder="vas@email.cz" />
                        </Field>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Trvalé bydliště</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Field label="Adresa (začněte psát pro vyhledání)" required>
                            <AddressSuggestInput
                                value={form.adresa_ulice}
                                onChange={v => setField('adresa_ulice', v)}
                                onSelectAddress={handleSelectAddress('adresa_')}
                                placeholder="Husova 10, Praha..."
                            />
                        </Field>
                    </div>
                    <Field label="PSČ" required><Input value={form.adresa_psc} onChange={v => setField('adresa_psc', v)} placeholder="12345" /></Field>
                    <Field label="Město" required><Input value={form.adresa_mesto} onChange={v => setField('adresa_mesto', v)} /></Field>
                    <Field label="Stát"><Input value={form.adresa_stat || 'Česká republika'} onChange={v => setField('adresa_stat', v)} /></Field>
                </div>
                <AddressValidation ulice={form.adresa_ulice} mesto={form.adresa_mesto} psc={form.adresa_psc} />
            </div>

            <Checkbox checked={sameAddress} onChange={(v) => {
                setSameAddress(v);
                if (v) {
                    setField('kontaktni_adresa_ulice', '');
                    setField('kontaktni_adresa_mesto', '');
                    setField('kontaktni_adresa_psc', '');
                    setField('kontaktni_adresa_stat', '');
                }
            }} label="Kontaktní adresa je stejná jako trvalá" />

            {!sameAddress && (
                <div>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Kontaktní adresa</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <Field label="Adresa (začněte psát pro vyhledání)">
                                <AddressSuggestInput
                                    value={form.kontaktni_adresa_ulice}
                                    onChange={v => setField('kontaktni_adresa_ulice', v)}
                                    onSelectAddress={handleSelectAddress('kontaktni_adresa_')}
                                    placeholder="Husova 10, Praha..."
                                />
                            </Field>
                        </div>
                        <Field label="PSČ"><Input value={form.kontaktni_adresa_psc} onChange={v => setField('kontaktni_adresa_psc', v)} placeholder="12345" /></Field>
                        <Field label="Město"><Input value={form.kontaktni_adresa_mesto} onChange={v => setField('kontaktni_adresa_mesto', v)} /></Field>
                        <Field label="Stát"><Input value={form.kontaktni_adresa_stat} onChange={v => setField('kontaktni_adresa_stat', v)} /></Field>
                    </div>
                    <AddressValidation ulice={form.kontaktni_adresa_ulice} mesto={form.kontaktni_adresa_mesto} psc={form.kontaktni_adresa_psc} />
                </div>
            )}
        </div>
    );
}

function StepEvidence({ form, setField, pojistovny, statniPrislusnosti }) {
    const rcParsed = parseRodneCislo(form.rodne_cislo);
    const rcAutoFilled = !!rcParsed;

    const handleRcChange = (v) => {
        setField('rodne_cislo', v);
        const parsed = parseRodneCislo(v);
        if (parsed) {
            setField('datum_narozeni', parsed.datum);
            setField('pohlavi', parsed.pohlavi);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Rodné číslo" required><Input value={form.rodne_cislo} onChange={handleRcChange} placeholder="000000/0000" /></Field>
            <Field label="Datum narození" required>
                <Input type="date" value={form.datum_narozeni} onChange={v => setField('datum_narozeni', v)} prefilled={rcAutoFilled} readOnly={rcAutoFilled} />
            </Field>
            <Field label="Místo narození" required><Input value={form.misto_narozeni} onChange={v => setField('misto_narozeni', v)} /></Field>
            <Field label="Pohlaví" required>
                {rcAutoFilled ? (
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium px-3 py-1.5 rounded-lg" style={{ background: 'var(--brand-bg)', border: '1px solid var(--brand)', color: 'var(--brand-text)' }}>
                            {form.pohlavi === 'muz' ? 'Muž' : 'Žena'}
                        </span>
                        <span className="text-[10px]" style={{ color: 'var(--brand)' }}>Dopočítáno z RČ</span>
                    </div>
                ) : (
                    <RadioGroup value={form.pohlavi} onChange={v => setField('pohlavi', v)} options={[
                        { value: 'muz', label: 'Muž' },
                        { value: 'zena', label: 'Žena' },
                    ]} />
                )}
            </Field>
            <Field label="Rodné příjmení"><Input value={form.rodne_prijmeni} onChange={v => setField('rodne_prijmeni', v)} placeholder="Pokud se liší" /></Field>
            <Field label="Národnost"><Input value={form.narodnost} onChange={v => setField('narodnost', v)} placeholder="Česká" /></Field>
            <Field label="Státní příslušnost" required>
                <Select value={form.statni_prislusnost_id} onChange={v => setField('statni_prislusnost_id', v)}
                    options={statniPrislusnosti.map(s => ({ value: s.id, label: s.nazev }))}
                    placeholder="Vyberte..."
                />
            </Field>
            <Field label="Zdravotní pojišťovna" required>
                <Select value={form.pojistovna_id} onChange={v => setField('pojistovna_id', v)}
                    options={pojistovny.map(p => ({ value: p.id, label: `${p.kod} - ${p.nazev}` }))}
                    placeholder="Vyberte..."
                />
            </Field>
            <Field label="Číslo občanského průkazu" required><Input value={form.cislo_dokladu} onChange={v => setField('cislo_dokladu', v)} placeholder="Číslo OP" /></Field>
            <Field label="Číslo řidičského průkazu"><Input value={form.cislo_ridicskeho_prukazu} onChange={v => setField('cislo_ridicskeho_prukazu', v)} placeholder="Číslo ŘP" /></Field>
        </div>
    );
}

function StepVzdelani({ form, setField }) {
    const showKkov = form.vzdelani && form.vzdelani !== 'Základní';

    const handleVzdelaniChange = (v) => {
        setField('vzdelani', v);
        if (v === 'Základní') {
            setField('vzdelani_kod', '');
            setField('obor_vzdelani', '');
        }
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Rodinný stav" required>
                    <Select value={form.rodinny_stav} onChange={v => setField('rodinny_stav', v)} options={RODINNY_STAV_OPTIONS} placeholder="Vyberte..." />
                </Field>
                <Field label="Nejvyšší dosažené vzdělání" required>
                    <Select value={form.vzdelani} onChange={handleVzdelaniChange} options={VZDELANI_OPTIONS} placeholder="Vyberte..." />
                </Field>
            </div>

            {showKkov && (
                <KkovSelect
                    vzdelani={form.vzdelani}
                    value={form.vzdelani_kod}
                    onChange={(kod, oborNazev) => {
                        setField('vzdelani_kod', kod);
                        if (oborNazev) setField('obor_vzdelani', oborNazev);
                    }}
                />
            )}

            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Informace o důchodu</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Typ důchodu">
                        <Select value={form.typ_duchodu} onChange={v => setField('typ_duchodu', v)} options={DUCHOD_OPTIONS} placeholder="Žádný" />
                    </Field>
                    {form.typ_duchodu && (
                        <>
                            <Field label="Datum důchodu"><Input type="date" value={form.datum_duchodu} onChange={v => setField('datum_duchodu', v)} /></Field>
                            <Checkbox checked={form.predcasny_duchod} onChange={v => setField('predcasny_duchod', v)} label="Předčasný důchod" />
                            <Checkbox checked={form.snizeny_vek} onChange={v => setField('snizeny_vek', v)} label="Snížený věk" />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StepFinance({ form, setField }) {
    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Bankovní spojení</h3>
                <div className="space-y-3">
                    <Checkbox checked={form.mzdy_na_ucet} onChange={v => {
                        setField('mzdy_na_ucet', v);
                        if (!v) setField('bankovni_ucet', '');
                    }} label="Přeji si zasílat mzdu na bankovní účet" />
                    {form.mzdy_na_ucet && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <Field label="Číslo účtu / Kód banky" required><Input value={form.bankovni_ucet} onChange={v => setField('bankovni_ucet', v)} placeholder="1234567890/0100" /></Field>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Prohlášení poplatníka daně z příjmů („růžové prohlášení").
                Stojí hned za bankovním spojením a před vyživovanými osobami,
                protože daňové zvýhodnění na děti se uplatňuje právě přes ně —
                bez podpisu je krok s dětmi mzdám k ničemu.

                Ano/Ne, ne zaškrtávátko: nezaškrtnuté políčko by se uložilo jako
                „ne", aniž by se kdokoli zeptal, a mzdy by podle toho počítaly. */}
            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Prohlášení k dani</h3>
                <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                    Podepíšete u nás <strong>Prohlášení poplatníka daně z příjmů ze závislé činnosti</strong> (tzv. růžové prohlášení)?
                    Uplatní se tím základní sleva na poplatníka a daňové zvýhodnění na vyživované děti.
                    Ve stejném období ho lze mít podepsané <strong>jen u jednoho zaměstnavatele</strong>.
                </p>
                <RadioGroup value={form.prohlaseni_k_dani} onChange={v => setField('prohlaseni_k_dani', v)} options={[
                    { value: 'ano', label: 'Ano, podepíšu' },
                    { value: 'ne', label: 'Ne (mám ho u jiného zaměstnavatele)' },
                ]} />
            </div>

            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Insolvence / Exekuce</h3>
                <RadioGroup value={form.insolvence_exekuce || 'nemam'} onChange={v => setField('insolvence_exekuce', v)} options={[
                    { value: 'nemam', label: 'Nemám' },
                    { value: 'exekuce', label: 'Exekuce' },
                    { value: 'insolvence', label: 'Insolvence' },
                ]} />
            </div>

            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Zdravotní omezení</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Typ omezení (I, II, III, OZZ)">
                        <Select value={form.zdravotni_omezeni_typ} onChange={v => setField('zdravotni_omezeni_typ', v)} options={ZDR_OMEZENI_OPTIONS} placeholder="Žádné" />
                    </Field>
                    {form.zdravotni_omezeni_typ && (
                        <>
                            <Field label="Omezení od"><Input type="date" value={form.zdravotni_omezeni_od} onChange={v => setField('zdravotni_omezeni_od', v)} /></Field>
                            <Field label="Omezení do"><Input type="date" value={form.zdravotni_omezeni_do} onChange={v => setField('zdravotni_omezeni_do', v)} /></Field>
                        </>
                    )}
                    <Checkbox checked={form.ztp_p} onChange={v => setField('ztp_p', v)} label="ZTP/P" />
                </div>
            </div>
        </div>
    );
}

function StepDeti({ form, setField }) {
    const osoby = form.vyzivovane_osoby || [];

    const addOsoba = () => {
        setField('vyzivovane_osoby', [...osoby, { jmeno_prijmeni: '', rodne_cislo: '', ztp_p: false }]);
    };
    const removeOsoba = (i) => {
        setField('vyzivovane_osoby', osoby.filter((_, idx) => idx !== i));
    };
    const updateOsoba = (i, key, val) => {
        const copy = [...osoby];
        copy[i] = { ...copy[i], [key]: val };
        setField('vyzivovane_osoby', copy);
    };

    return (
        <div className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Zadejte údaje o vyživovaných osobách / dětech. Pokud žádné nemáte, pokračujte dál.
            </p>

            {osoby.map((o, i) => (
                <div key={i} className="p-4 rounded-lg flex gap-3 items-start" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label="Jméno a příjmení"><Input value={o.jmeno_prijmeni} onChange={v => updateOsoba(i, 'jmeno_prijmeni', v)} /></Field>
                        <Field label="Rodné číslo"><Input value={o.rodne_cislo} onChange={v => updateOsoba(i, 'rodne_cislo', v)} /></Field>
                        <div className="flex items-end">
                            <Checkbox checked={o.ztp_p} onChange={v => updateOsoba(i, 'ztp_p', v)} label="ZTP/P" />
                        </div>
                    </div>
                    <button type="button" onClick={() => removeOsoba(i)} className="mt-5 p-1.5 rounded-lg" style={{ color: '#f87171' }}>
                        <Trash2 size={16} />
                    </button>
                </div>
            ))}

            <button
                type="button"
                onClick={addOsoba}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                style={{ color: 'var(--brand-text)', border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}
            >
                <Plus size={14} /> Přidat osobu
            </button>
        </div>
    );
}

function StepPodpis({ form, setField, pojistovny, statniPrislusnosti }) {
    const fmtDate = (d) => {
        if (!d) return '—';
        const [y, m, day] = d.split('-');
        return `${day}.${m}.${y}`;
    };

    const pojistovnaLabel = pojistovny?.find(p => String(p.id) === String(form.pojistovna_id));
    const spLabel = statniPrislusnosti?.find(s => String(s.id) === String(form.statni_prislusnost_id));

    const sections = [
        { title: 'Osobní údaje', items: [
            ['Jméno', [form.titul_pred, form.jmeno, form.prijmeni, form.titul_za].filter(Boolean).join(' ')],
            ['Telefon', form.telefon],
            ['Kontaktní e-mail', form.kontaktni_email],
        ]},
        { title: 'Bydliště', items: [
            ['Trvalé bydliště', [form.adresa_ulice, form.adresa_psc, form.adresa_mesto].filter(Boolean).join(', ')],
            ['Stát', form.adresa_stat],
            ...(form.kontaktni_adresa_ulice || form.kontaktni_adresa_mesto ? [
                ['Kontaktní adresa', [form.kontaktni_adresa_ulice, form.kontaktni_adresa_psc, form.kontaktni_adresa_mesto].filter(Boolean).join(', ')],
                ['Kontaktní stát', form.kontaktni_adresa_stat],
            ] : []),
        ]},
        { title: 'Evidenční údaje', items: [
            ['Rodné číslo', form.rodne_cislo],
            ['Datum narození', fmtDate(form.datum_narozeni)],
            ['Místo narození', form.misto_narozeni],
            ['Pohlaví', form.pohlavi === 'muz' ? 'Muž' : 'Žena'],
            ['Rodné příjmení', form.rodne_prijmeni],
            ['Národnost', form.narodnost],
            ['Státní příslušnost', spLabel?.nazev],
            ['Zdravotní pojišťovna', pojistovnaLabel ? `${pojistovnaLabel.kod} - ${pojistovnaLabel.nazev}` : ''],
            ['Číslo OP', form.cislo_dokladu],
            ['Číslo ŘP', form.cislo_ridicskeho_prukazu],
        ]},
        { title: 'Vzdělání', items: [
            ['Rodinný stav', form.rodinny_stav],
            ['Vzdělání', form.vzdelani],
            ['Kód KKOV', form.vzdelani_kod],
            ['Obor vzdělání', form.obor_vzdelani],
        ]},
        { title: 'Důchod', items: [
            ['Typ důchodu', form.typ_duchodu || 'Žádný'],
            ...(form.typ_duchodu ? [
                ['Datum důchodu', fmtDate(form.datum_duchodu)],
                ['Předčasný důchod', form.predcasny_duchod ? 'Ano' : 'Ne'],
                ['Snížený věk', form.snizeny_vek ? 'Ano' : 'Ne'],
            ] : []),
        ]},
        { title: 'Finance a právní', items: [
            ['Mzdy na účet', form.mzdy_na_ucet ? 'Ano' : 'Ne'],
            ...(form.mzdy_na_ucet ? [['Bankovní účet', form.bankovni_ucet]] : []),
            ['Prohlášení k dani', form.prohlaseni_k_dani === 'ano' ? 'Ano, podepíšu' : (form.prohlaseni_k_dani === 'ne' ? 'Ne' : '—')],
            ['Insolvence/exekuce', (form.insolvence_exekuce || 'nemam').charAt(0).toUpperCase() + (form.insolvence_exekuce || 'nemam').slice(1)],
        ]},
        { title: 'Zdravotní omezení', items: [
            ['Typ omezení', form.zdravotni_omezeni_typ || 'Žádné'],
            ...(form.zdravotni_omezeni_typ ? [
                ['Omezení od', fmtDate(form.zdravotni_omezeni_od)],
                ['Omezení do', fmtDate(form.zdravotni_omezeni_do)],
            ] : []),
            ['ZTP/P', form.ztp_p ? 'Ano' : 'Ne'],
        ]},
    ];

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Souhrn zadaných údajů</h3>
                <div className="rounded-lg p-4 space-y-4" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                    {sections.map((section) => (
                        <div key={section.title}>
                            <h4 className="text-xs font-semibold mb-1.5" style={{ color: 'var(--brand)' }}>{section.title}</h4>
                            <div className="space-y-1">
                                {section.items.map(([label, val]) => (
                                    <div key={label} className="flex justify-between text-sm">
                                        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                                        <span style={{ color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{val || '—'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    {(form.vyzivovane_osoby || []).length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold mb-1.5" style={{ color: 'var(--brand)' }}>Vyživované osoby</h4>
                            {form.vyzivovane_osoby.map((o, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                    <span style={{ color: 'var(--text-muted)' }}>{o.jmeno_prijmeni || `Osoba ${i + 1}`}</span>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                        {[o.rodne_cislo, o.ztp_p ? 'ZTP/P' : ''].filter(Boolean).join(', ') || '—'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Váš podpis</h3>
                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    Nakreslete svůj podpis myší nebo prstem na dotykové obrazovce.
                </p>
                <SignaturePad value={form.podpis} onChange={v => setField('podpis', v)} />
            </div>

            <div className="rounded-lg p-4" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Prohlašuji, že všechny mnou uvedené údaje jsou pravdivé a že souhlasím s tím,
                    aby byly zpracovány a použity pro účely personalistiky na dobu neurčitou (tj. do odvolání)
                    v rámci činnosti zaměstnavatele. Zároveň prohlašuji, že se zavazuji každou změnu údajů
                    uvedených v tomto dotazníku nahlásit do 5 pracovních dnů od data změny.
                </p>
            </div>
        </div>
    );
}

// ── Required fields per step for validation ─────────────────
const REQUIRED_BY_STEP = {
    osobni:   ['telefon'],
    adresa:   ['adresa_ulice', 'adresa_mesto', 'adresa_psc', 'kontaktni_email'],
    evidence: ['rodne_cislo', 'datum_narozeni', 'misto_narozeni', 'pohlavi', 'statni_prislusnost_id', 'pojistovna_id', 'cislo_dokladu'],
    vzdelani: ['rodinny_stav', 'vzdelani'],
    finance:  ['insolvence_exekuce', 'prohlaseni_k_dani'],
    deti:     [],
    podpis:   ['podpis'],
};

// ═══════════════════════════════════════════════════════════
// ── Main Modal ────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════
export default function OnboardingModal({ user, onComplete, onLogout }) {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        jmeno: '', prijmeni: '', email: '', kontaktni_email: '',
        titul_pred: '', titul_za: '', telefon: '',
        adresa_ulice: '', adresa_mesto: '', adresa_psc: '', adresa_stat: 'Česká republika',
        kontaktni_adresa_ulice: '', kontaktni_adresa_mesto: '', kontaktni_adresa_psc: '', kontaktni_adresa_stat: '',
        rodne_cislo: '', datum_narozeni: '', misto_narozeni: '', pohlavi: '',
        rodne_prijmeni: '', narodnost: '', statni_prislusnost_id: '', pojistovna_id: '',
        cislo_dokladu: '', cislo_ridicskeho_prukazu: '',
        rodinny_stav: '', vzdelani: '', obor_vzdelani: '', vzdelani_kod: '',
        typ_duchodu: '', datum_duchodu: '', predcasny_duchod: false, snizeny_vek: false,
        insolvence_exekuce: 'nemam',
        zdravotni_omezeni_typ: '', zdravotni_omezeni_od: '', zdravotni_omezeni_do: '', ztp_p: false,
        mzdy_na_ucet: false,
        bankovni_ucet: '',
        prohlaseni_k_dani: '',
        vyzivovane_osoby: [],
        podpis: '',
    });
    const [pojistovny, setPojistovny] = useState([]);
    const [statniPrislusnosti, setStatniPrislusnosti] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [stepErrors, setStepErrors] = useState({});

    // Load initial data
    useEffect(() => {
        Promise.all([
            fetch('/api/me/onboarding-status').then(r => r.json()),
            fetch('/api/ciselniky/pojistovny').then(r => r.json()),
            fetch('/api/ciselniky/statni-prislusnosti').then(r => r.json()),
        ]).then(([status, poj, sp]) => {
            setForm(f => ({
                ...f,
                jmeno: status.jmeno || '',
                prijmeni: status.prijmeni || '',
                email: status.email || '',
            }));
            setPojistovny(poj);
            setStatniPrislusnosti(sp);
        });

    }, []);

    const setField = (key, val) => {
        setForm(f => ({ ...f, [key]: val }));
        setStepErrors({});
    };

    const validateStep = (stepIdx) => {
        const stepId = STEPS[stepIdx].id;

        // Podmíněná validace: mzdy_na_ucet → bankovni_ucet povinný
        if (stepId === 'finance' && form.mzdy_na_ucet && !form.bankovni_ucet) {
            setStepErrors({ bankovni_ucet: true });
            return false;
        }

        const required = REQUIRED_BY_STEP[stepId] || [];
        const missing = required.filter(key => {
            const val = form[key];
            return val === '' || val === null || val === undefined;
        });
        if (missing.length > 0) {
            setStepErrors(Object.fromEntries(missing.map(k => [k, true])));
            return false;
        }
        setStepErrors({});
        return true;
    };

    const goNext = () => {
        if (!validateStep(step)) return;
        if (step < STEPS.length - 1) setStep(step + 1);
    };
    const goBack = () => { if (step > 0) setStep(step - 1); };

    const handleSubmit = async () => {
        if (submitting) return;
        if (!validateStep(step)) return;
        setSubmitting(true);
        setError('');

        try {
            const token = document.querySelector('meta[name="csrf-token"]')?.content;
            // Prohlášení k dani se ve formuláři drží jako 'ano'/'ne' (RadioGroup
            // pracuje s řetězci), na server ale patří jako boolean. Bez volby se
            // sem nedá dojít — krok Finance ji má mezi povinnými — takže se
            // `undefined` neposílá a `sometimes` validace na backendu drží.
            const data = { ...form, prohlaseni_k_dani: form.prohlaseni_k_dani === 'ano' };
            const resp = await fetch('/api/me/onboarding', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify(data),
            });

            if (!resp.ok) {
                const data = await resp.json();
                throw new Error(data.message || data.error || 'Chyba při odesílání');
            }

            onComplete();
        } catch (e) {
            setError(e.message);
            setSubmitting(false);
        }
    };

    const currentStep = STEPS[step];
    const isLastStep = step === STEPS.length - 1;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px',
            }}
        >
            <div
                style={{
                    background: 'var(--surface-raised)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '16px',
                    boxShadow: 'var(--shadow-lg)',
                    width: '100%',
                    maxWidth: '680px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div className="shrink-0 px-6 pt-5 pb-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Osobní dotazník</h2>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Vyplňte prosím všechny povinné údaje pro zpracování pracovní smlouvy.</p>
                        </div>
                        {onLogout && (
                            <button
                                type="button"
                                onClick={onLogout}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium shrink-0"
                                style={{
                                    color: 'var(--text-muted)',
                                    border: '1px solid var(--border-default)',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                            >
                                <LogOut size={13} /> Odhlásit se
                            </button>
                        )}
                    </div>

                    {/* Stepper */}
                    <div className="mt-4">
                        <div className="flex items-center" style={{ padding: '0 4px' }}>
                            {STEPS.map((s, i) => {
                                const done = i < step;
                                const active = i === step;
                                return (
                                    <div key={s.id} className="flex items-center" style={{ flex: i < STEPS.length - 1 ? 1 : 'none' }}>
                                        <div
                                            style={{
                                                width: active ? 28 : 24,
                                                height: active ? 28 : 24,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                flexShrink: 0,
                                                transition: 'all 0.25s ease',
                                                background: active ? 'var(--brand)' : done ? '#10b981' : 'transparent',
                                                border: `2px solid ${active ? 'var(--brand)' : done ? '#10b981' : 'var(--border-strong)'}`,
                                                color: active ? '#1a1a2e' : done ? 'white' : 'var(--text-muted)',
                                                boxShadow: active ? '0 0 0 4px var(--brand-bg)' : 'none',
                                            }}
                                        >
                                            {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                                        </div>
                                        {i < STEPS.length - 1 && (
                                            <div style={{
                                                flex: 1,
                                                height: '2px',
                                                margin: '0 4px',
                                                borderRadius: '1px',
                                                background: 'var(--border-default)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}>
                                                <div style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    background: '#10b981',
                                                    transform: `scaleX(${done ? 1 : 0})`,
                                                    transformOrigin: 'left',
                                                    transition: 'transform 0.3s ease',
                                                    borderRadius: '1px',
                                                }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-center mt-2" style={{ color: 'var(--brand)', fontSize: '12px', fontWeight: 600 }}>
                            {currentStep.label}
                        </p>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarWidth: 'thin' }}>
                    {Object.keys(stepErrors).length > 0 && (
                        <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}>
                            Vyplňte prosím všechna povinná pole označená *.
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}>
                            {error}
                        </div>
                    )}

                    {currentStep.id === 'osobni'   && <StepOsobni form={form} setField={setField} />}
                    {currentStep.id === 'adresa'    && <StepAdresa form={form} setField={setField} />}
                    {currentStep.id === 'evidence'  && <StepEvidence form={form} setField={setField} pojistovny={pojistovny} statniPrislusnosti={statniPrislusnosti} />}
                    {currentStep.id === 'vzdelani'  && <StepVzdelani form={form} setField={setField} />}
                    {currentStep.id === 'finance'   && <StepFinance form={form} setField={setField} />}
                    {currentStep.id === 'deti'      && <StepDeti form={form} setField={setField} />}
                    {currentStep.id === 'podpis'    && <StepPodpis form={form} setField={setField} pojistovny={pojistovny} statniPrislusnosti={statniPrislusnosti} />}
                </div>

                {/* Footer navigation */}
                <div className="shrink-0 flex items-center justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-default)' }}>
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={step === 0}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
                        style={{
                            color: step === 0 ? 'var(--text-muted)' : 'var(--text-secondary)',
                            cursor: step === 0 ? 'not-allowed' : 'pointer',
                            background: 'transparent',
                            border: '1px solid var(--border-default)',
                            opacity: step === 0 ? 0.5 : 1,
                        }}
                    >
                        <ChevronLeft size={14} /> Zpět
                    </button>

                    {isLastStep ? (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || !form.podpis}
                            className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold"
                            style={{
                                background: (!form.podpis || submitting) ? 'var(--surface-elevated)' : 'var(--brand)',
                                color: (!form.podpis || submitting) ? 'var(--text-muted)' : '#1a1a2e',
                                cursor: (!form.podpis || submitting) ? 'not-allowed' : 'pointer',
                                border: 'none',
                            }}
                        >
                            {submitting ? <><Loader2 size={16} className="animate-spin" /> Odesílám...</> : 'Odeslat dotazník'}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={goNext}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold"
                            style={{
                                background: 'var(--brand)',
                                color: '#1a1a2e',
                                cursor: 'pointer',
                                border: 'none',
                            }}
                        >
                            Další <ChevronRight size={14} />
                        </button>
                    )}
                </div>
            </div>

        </div>
    );
}
